import { useMediaPicker } from "@/app-bridge/hooks/useMediaPicker";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ImagePlus, LifeBuoy, Loader2, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OtpCodeField } from "@/components/ui/otp-code-field";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "@tanstack/react-router";
import { SUPPORT_CATEGORIES } from "@/lib/support";
import { helpMatches } from "@/lib/help-articles";
import { compressImage } from "@/lib/image-compress";
import { useSurfaceWords } from "@/lib/surface";
import { useLocale } from "@/lib/i18n";
import { localizedPath } from "@/lib/i18n/slug-map";
import { supportDialogText } from "@/lib/i18n/support-copy";
import { requestSupportCode, submitGuestTicket, submitTicket } from "@/lib/support.functions";

type Attachment = { base64: string; mimeType: string; preview: string };

async function toAttachment(file: File): Promise<Attachment> {
  const blob = await compressImage(file, 1400, 0.75);
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Kunde inte läsa bilden"));
    reader.readAsDataURL(blob);
  });
  return {
    base64: dataUrl.split(",")[1] ?? "",
    mimeType: blob.type || "image/jpeg",
    preview: dataUrl,
  };
}

/** Samlar teknisk information som annars kräver en massa frågor fram och tillbaka. */
function collectContext(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  return {
    path: window.location.pathname + window.location.search,
    userAgent: navigator.userAgent,
    language: navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    pwa: window.matchMedia("(display-mode: standalone)").matches,
    online: navigator.onLine,
    time: new Date().toISOString(),
  };
}

export function SupportDialog({
  open,
  onOpenChange,
  defaultCategory = "other",
  defaultSubject = "",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCategory?: string;
  /** Förifylld rubrik, t.ex. det man nyss sökte på i hjälpcentret. */
  defaultSubject?: string;
}) {
  const words = useSurfaceWords();
  const { lang } = useLocale();
  const tx = supportDialogText[lang];
  const { user } = useAuth();
  const [category, setCategory] = useState(defaultCategory);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [done, setDone] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { pickImages, sheet: mediaSheet } = useMediaPicker();

  /** Lägger till valda bilder som bilagor, max tre stycken. */
  const addAttachments = async (picked: File[]) => {
    for (const file of picked.slice(0, 3 - files.length)) {
      try {
        const att = await toAttachment(file);
        setFiles((prev) => [...prev, att]);
      } catch {
        toast.error(tx.imageError);
      }
    }
  };

  // Öppnas modalen från en sökning i hjälpcentret fyller vi i frågan åt användaren.
  useEffect(() => {
    if (open && defaultSubject) setSubject((cur) => cur || defaultSubject);
  }, [open, defaultSubject]);

  const suggestions = useMemo(() => helpMatches(`${subject} ${body}`, 3, lang), [subject, body, lang]);

  const sendCode = useServerFn(requestSupportCode);
  const sendAuthed = useServerFn(submitTicket);
  const sendGuest = useServerFn(submitGuestTicket);

  const codeMutation = useMutation({
    mutationFn: () => sendCode({ data: { email: email.trim() } }),
    onSuccess: () => {
      setCodeSent(true);
      toast.success(tx.codeSent, { description: tx.checkEmail });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submit = useMutation({
    mutationFn: async () => {
      const payload = {
        category,
        subject: subject.trim(),
        body: body.trim(),
        context: collectContext(),
        attachments: files.map((f) => ({ base64: f.base64, mimeType: f.mimeType })),
      };
      if (user) return sendAuthed({ data: payload });
      return sendGuest({
        data: { ...payload, email: email.trim(), name: name.trim(), code: code.trim() },
      });
    },
    onSuccess: (res) => setDone(res.ticketNo),
    onError: (e: Error) => toast.error(e.message),
  });

  const reset = () => {
    setSubject(defaultSubject);
    setBody("");
    setCode("");
    setCodeSent(false);
    setFiles([]);
    setDone(null);
  };

  const canSubmit =
    subject.trim().length >= 3 &&
    body.trim().length >= 10 &&
    (user ? true : /\S+@\S+\.\S+/.test(email) && code.trim().length >= 3);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        {done ? (
          <div className="py-4 text-center">
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
              <LifeBuoy className="h-6 w-6" />
            </span>
            <h2 className="mt-4 font-heading text-xl font-bold">{tx.thanks}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {tx.ticketNumber} <strong>#{done}</strong>. {tx.responseTime}
            </p>
            <div className="mt-4 rounded-xl border border-border bg-muted/40 p-3 text-left text-sm text-muted-foreground">
              <p className="font-semibold text-foreground">{tx.howReply}</p>
              {user ? (
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  {tx.authedReplies.map((item, index) => <li key={item}>{index === 1 ? item.replace(/ här| here| aquí/, ` ${words.here}`) : item}</li>)}
                </ul>
              ) : (
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  <li>{tx.guestReplyBefore} {email || tx.yourEmail}.</li>
                  {tx.guestReplies.map((item) => <li key={item}>{item}</li>)}
                </ul>
              )}
            </div>
            {user && (
              <Link
                to="/mina-arenden"
                onClick={() => {
                  reset();
                  onOpenChange(false);
                }}
                className="mt-4 inline-block text-sm font-semibold text-primary hover:underline"
              >
                {tx.goTickets}
              </Link>
            )}
            <Button
              className="mt-6 w-full"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              {tx.close}
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">{tx.title}</DialogTitle>
              <DialogDescription>
                {tx.description}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="support-category">{tx.category}</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger id="support-category" className="h-10 w-full">
                    <SelectValue placeholder={tx.chooseCategory} />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORT_CATEGORIES.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.labels[lang]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="support-subject">{tx.subject}</Label>
                <Input
                  id="support-subject"
                  value={subject}
                  maxLength={140}
                  placeholder={tx.subjectPlaceholder}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="support-body">{tx.body}</Label>
                <Textarea
                  id="support-body"
                  value={body}
                  rows={5}
                  maxLength={5000}
                  placeholder={tx.bodyPlaceholder}
                  onChange={(e) => setBody(e.target.value)}
                />
              </div>

              {suggestions.length > 0 && (
                <div className="rounded-xl border border-border bg-muted/40 p-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {tx.suggestions}
                  </p>
                  <Accordion type="single" collapsible>
                    {suggestions.map((s) => (
                      <AccordionItem key={s.id} value={s.id} className="border-b-0">
                        <AccordionTrigger className="py-2 text-left text-sm">
                          {s.title}
                        </AccordionTrigger>
                        <AccordionContent className="space-y-2 text-sm text-muted-foreground">
                          <p>{s.summary}</p>
                          <Link
                            to={localizedPath("/support", lang)}
                            search={{ fraga: s.id }}
                            onClick={() => onOpenChange(false)}
                            className="inline-block font-semibold text-primary hover:underline"
                          >
                            {tx.readAnswer}
                          </Link>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}

              <div className="space-y-2">
                <Label>{tx.screenshots}</Label>
                <div className="flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <div key={i} className="relative">
                      <img
                        src={f.preview}
                        alt=""
                        className="h-16 w-16 rounded-md border border-border object-cover"
                      />
                      <button
                        type="button"
                        aria-label={tx.removeImage}
                        onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                        className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-background text-muted-foreground shadow"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  {files.length < 3 && (
                    <button
                      type="button"
                      onClick={() =>
                        pickImages({
                          multiple: true,
                          maxImages: 3 - files.length,
                          onFiles: (picked) => void addAttachments(picked),
                          openWebPicker: () => fileRef.current?.click(),
                        })
                      }
                      className="grid h-16 w-16 place-items-center rounded-md border border-dashed border-border text-muted-foreground"
                    >
                      <ImagePlus className="h-5 w-5" />
                    </button>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={async (e) => {
                    const picked = Array.from(e.target.files ?? []);
                    e.target.value = "";
                    await addAttachments(picked);
                  }}
                />
                {mediaSheet}
              </div>

              {!user && (
                <div className="space-y-3 rounded-xl border border-border p-3">
                  <p className="text-sm text-muted-foreground">
                    {tx.signedOut}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="support-name">{tx.name}</Label>
                      <Input
                        id="support-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={tx.namePlaceholder}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="support-email">{tx.email}</Label>
                      <Input
                        id="support-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={tx.emailPlaceholder}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="block text-center">{tx.emailCode}</Label>
                    <OtpCodeField
                      value={code}
                      readClipboard={codeSent}
                      label={tx.codeLabel}
                      onChange={setCode}
                      onComplete={() => {}}
                    />
                    <div className="flex justify-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!/\S+@\S+\.\S+/.test(email) || codeMutation.isPending}
                        onClick={() => codeMutation.mutate()}
                      >
                        {codeMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : codeSent ? (
                          tx.resendCode
                        ) : (
                          tx.sendCode
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <Button
                className="w-full"
                disabled={!canSubmit || submit.isPending}
                onClick={() => submit.mutate()}
              >
                {submit.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {tx.submit}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
